@file:Suppress(
    "RETURN_VALUE_NOT_USED",
    "RETURN_VALUE_NOT_USED_COERCION",
)

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Suppress
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import com.punyo.portfolio.components.layouts.MarkdownLayout as _com_punyo_portfolio_components_layouts_MarkdownLayout
import com.punyo.portfolio.components.layouts.PageLayout as _com_punyo_portfolio_components_layouts_PageLayout
import com.punyo.portfolio.pages.AboutPage as _com_punyo_portfolio_pages_AboutPage
import com.punyo.portfolio.pages.HomePage as _com_punyo_portfolio_pages_HomePage
import com.punyo.portfolio.pages.TimelinePage as _com_punyo_portfolio_pages_TimelinePage
import com.punyo.portfolio.pages.WorksPage as _com_punyo_portfolio_pages_WorksPage
import com.punyo.portfolio.pages.works.NitechvacancyviewerPage as _com_punyo_portfolio_pages_works_NitechvacancyviewerPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Portfolio"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "com.punyo.portfolio.components.layouts.MarkdownLayout",
            parentLayoutId = "com.punyo.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.punyo.portfolio.components.layouts.initMarkdownLayout(ctx)
            },
        ) { pageCtx, pageMethod -> 
            _com_punyo_portfolio_components_layouts_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "com.punyo.portfolio.components.layouts.PageLayout",
        ) { pageCtx, pageMethod -> 
            _com_punyo_portfolio_components_layouts_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "com.punyo.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.punyo.portfolio.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _com_punyo_portfolio_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "com.punyo.portfolio.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.punyo.portfolio.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _com_punyo_portfolio_pages_AboutPage()
        }
        ctx.router.register(
            "/timeline",
            layoutId = "com.punyo.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.punyo.portfolio.pages.initTimelinePage(ctx)
            },
        ) { pageCtx -> 
            _com_punyo_portfolio_pages_TimelinePage()
        }
        ctx.router.register(
            "/works",
            layoutId = "com.punyo.portfolio.components.layouts.PageLayout",
            initRouteMethod = { ctx ->
                com.punyo.portfolio.pages.initWorksPage(ctx)
            },
        ) { pageCtx -> 
            _com_punyo_portfolio_pages_WorksPage()
        }
        ctx.router.register(
            "/works/nitechvacancyviewer",
            layoutId = "com.punyo.portfolio.components.layouts.MarkdownLayout",
            initRouteMethod = { ctx ->
                com.punyo.portfolio.pages.works.initNitechvacancyviewerPage(ctx)
            },
        ) { pageCtx -> 
            _com_punyo_portfolio_pages_works_NitechvacancyviewerPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        ctx.theme.registerStyle("headline-text", com.punyo.portfolio.HeadlineTextStyle)
        ctx.theme.registerStyle("subheadline-text", com.punyo.portfolio.SubheadlineTextStyle)
        ctx.theme.registerStyle("nav-header", com.punyo.portfolio.components.sections.NavHeaderStyle)
        ctx.theme.registerStyle("hero-section", com.punyo.portfolio.components.sections.HeroSectionStyle)
        ctx.theme.registerStyle("page-content", com.punyo.portfolio.components.layouts.PageContentStyle)
        ctx.theme.registerStyle("markdown", com.punyo.portfolio.components.layouts.MarkdownStyle)
        ctx.theme.registerVariant("-circle", com.punyo.portfolio.CircleButtonVariant
        )
        ctx.theme.registerVariant("-uncolored", com.punyo.portfolio.UncoloredButtonVariant
        )
        ctx.theme.registerKeyframes("side-menu-slide-in", com.punyo.portfolio.components.sections.SideMenuSlideInAnim)
        com.punyo.portfolio.initTheme(ctx)
        com.punyo.portfolio.initSiteStyles(ctx)
        com.punyo.portfolio.initColorMode(ctx)
        com.punyo.portfolio.initStyles(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        com.punyo.portfolio.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
